import '../ProductCard.scss';
import './MobileProductCard.scss';

import classnames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import loadable from '@loadable/component';

import { listerClickThrough } from '../../../actions/lister-actions';
import { useEventListener } from '../../../hooks/useEventListener';
import TRANSLATIONS from '../../../translations/translations@default.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
} from '../../../utilities/lister-utilities';
import { Context } from '../../Views/store/store';
import Bullets from '../Bullets';
import EnergyInformation from '../EnergyInformation/EnergyInformation';
import ExVat from '../ExVat';
import Heading from '../Heading';
import PricePod from '../PricePod/PricePod@default';
import ProductImage from '../ProductImage/ProductImage';
import PromoFlag from '../PromoFlag';
import ReviewRating from '../ReviewRating/ReviewRating';
import StockStatus from '../StockStatus';
import MobileProductCardSkeleton from './Skeletons/MobileProductCardSkeleton';

const BackInStockForm = loadable(() =>
	import(/* webpackPrefetch: true */ '../BackInStockForm/BackInStockForm.js')
);

export const MobileProductCard = ({ index, product, observer, page }) => {
	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	const dispatch = useDispatch();

	const card = useRef(null);

	useEffect(() => {
		const cardref = card.current;

		if (observer && cardref) {
			observer.observe(cardref);
		}

		return () => {
			if (observer && cardref) {
				observer.unobserve(cardref);
			}
		};
	}, [card.current, observer]);

	if (!product) {
		return null;
	}

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);

	if (!loaded) {
		return <MobileProductCardSkeleton ColourId={product.ColourId} />;
	}

	return (
		<li
			ref={card}
			data-page={page}
			id={product.Code}
			className="xs:p-2 w-full mobile-outer"
			style={{ WebkitTransform: 'translateZ(0)' }}
			itemProp="itemListElement"
			itemScope
			itemType="http://schema.org/ListItem"
			data-testid={
				product.IsFeaturedProduct
					? 'product-card--featured'
					: 'product-card--standard'
			}>
			<meta itemProp="position" content={index} />
			<div
				className={classnames('product-card', 'mobile-border', {
					'product-card--out-of-stock': product.State === 'OutOfStock',
				})}
				data-testid={
					product.State === 'OutOfStock'
						? 'product-state--out-of-stock'
						: 'product-state--available'
				}>
				<a
					href={product.FullProductUrl}
					onClick={(event) => {
						event.preventDefault();
						window.location.assign(product.FullProductUrl);

						dispatch(listerClickThrough(product.Code));
					}}
					className="product-card__link"
					itemProp="url"
					data-testid="product-card-link"
					data-tag-name="lister product card"
					data-tag-type={product.IsFeaturedProduct ? 'featured' : 'standard'}
					data-tag-delivery-date={deliveryDate}
					data-tag-delivery-days={deliveryDaysDifference}>
					<span>
						{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
					</span>
				</a>

				<div
					className="mobile-border flex bg-white p-4 xs:rounded-lg relative border border-solid border-gray-30"
					itemScope
					itemType="http://schema.org/Product"
					data-productcode={product.Code}>
					<div
						className="flex flex-col justify-between w-1/3 p-2 pl-0"
						data-testid="product-card-mobile-view">
						<ProductImage {...product} />

						<StockStatus
							classes="mt-4"
							stockStatus={product.State}
							productCode={product.Code}
							alignCenter
						/>

						{product.EnergyRating && (
							<EnergyInformation
								classes="mt-4 text-center z-10"
								energyRatingDetails={product.EnergyRatingDetails}
							/>
						)}
					</div>
					<div className="w-2/3 p-2 pr-0">
						<Heading content={product.FullTitle} />
						<ReviewRating classes="mt-2" {...product} />

						<React.Fragment>
							<PricePod
								viewModel={product.PricePodViewModel}
								inStock={product.State !== 'OutOfStock'}
								{...product}
							/>

							<ExVat />
						</React.Fragment>

						<PromoFlag {...product} />

						<Bullets content={product.FeatureDescriptions} />
					</div>
				</div>

				{product.State === 'OutOfStock' && (
					<BackInStockForm
						productCode={product.Code}
						productUrl={product.ProductUrl}
						productColourId={product.ColourId}
						productState={product.State}
					/>
				)}
			</div>
		</li>
	);
};

export default MobileProductCard;
